import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { CaretRight, CaretDown, FolderSimple, FileText, Copy, Check } from '@phosphor-icons/react';
import './Hierarchy.scss';

type TreeNode = {
  name: string;
  kind?: string;
  note?: string;
  children?: TreeNode[];
};

const TREE: TreeNode = {
  name: 'MainScene',
  children: [
    {
      name: '[Environment & Scene Base]',
      kind: '씬 기본 환경 세팅',
      children: [
        { name: 'Main Camera', kind: 'Camera Object', note: 'Screen Space - Overlay/Camera 설정' },
        { name: 'Global Light 2D', kind: 'Light 2D Object', note: '2D 프로젝트 기본 광원 (인게임 연출용)' },
      ],
    },
    {
      name: '[Managers]',
      kind: 'Empty : Scene Managers Parent',
      children: [
        { name: 'AppManager', kind: 'Empty + C# Script', note: '앱 생명주기 및 사용자 데이터 관리' },
        { name: 'AuthManager', kind: 'Empty + C# Script', note: '구글 로그인 연동' },
        { name: 'UIManager', kind: 'Empty + C# Script', note: '화면 전환 및 Panel Active 제어' },
        { name: 'SoundManager', kind: 'Empty + AudioSource + C# Script', note: 'BGM, 드럼 타격음, 효과음' },
        { name: 'GameManager', kind: 'Empty + C# Script', note: '노트 판정, 인지 게임 로직 및 스코어링' },
      ],
    },
    {
      name: 'MainUI_Canvas',
      kind: 'Canvas (Sort Order: 0)',
      children: [
        {
          name: 'SafeArea',
          kind: 'Empty + RectTransform (SafeArea Script)',
          note: '모바일 노치 대응',
          children: [
            {
              name: 'FullScreen_Container',
              kind: 'Empty + RectTransform (Full Screen)',
              note: '하단 탭 없는 영역',
              children: [
                {
                  name: 'Login_Panel',
                  kind: 'Empty / Image (Background)',
                  note: '1. 구글 로그인 화면',
                  children: [
                    { name: 'Img_Logo', kind: 'Image', note: '앱 로고' },
                    { name: 'Txt_Welcome', kind: 'Text (TMP)', note: '안내 문구' },
                    {
                      name: 'Btn_GoogleLogin',
                      kind: 'Button (TMP)',
                      note: '구글 로그인 버튼',
                      children: [
                        { name: 'Img_GoogleIcon', kind: 'Image' },
                        { name: 'Txt_GoogleLogin', kind: 'Text (TMP)' },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              name: 'MainContent_Container',
              kind: 'Empty + RectTransform',
              note: '하단 탭 포함 메인 영역',
              children: [
                {
                  name: 'SongList_Panel',
                  kind: 'Empty',
                  note: '2-1. 하단 탭1 (DRUM) - 곡 리스트 화면',
                  children: [
                    {
                      name: 'TopBar',
                      kind: 'Image / Empty',
                      children: [
                        { name: 'Txt_Title', kind: 'Text (TMP)', note: '"곡 선택"' },
                        {
                          name: 'Btn_MyPage',
                          kind: 'Button (TMP)',
                          note: '마이페이지 이동 버튼',
                          children: [{ name: 'Img_ProfileIcon', kind: 'Image' }],
                        },
                      ],
                    },
                    {
                      name: 'SongList_ScrollView',
                      kind: 'ScrollView',
                      note: '곡 목록 리스트',
                      children: [
                        {
                          name: 'Viewport',
                          kind: 'Mask / RectMask2D',
                          children: [
                            {
                              name: 'Content',
                              kind: 'Empty + VerticalLayoutGroup',
                              children: [
                                {
                                  name: '[Prefab] SongItem',
                                  kind: 'Image / Button',
                                  note: '곡 선택 카드',
                                  children: [
                                    { name: 'Img_AlbumCover', kind: 'Image' },
                                    { name: 'Txt_SongTitle', kind: 'Text (TMP)' },
                                    { name: 'Txt_Artist', kind: 'Text (TMP)' },
                                    { name: 'Btn_SelectSong', kind: 'Button (TMP)' },
                                  ],
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      name: 'DifficultySelect_Modal',
                      kind: 'Empty / Image',
                      note: '곡 선택 시 난이도 선택 모달',
                      children: [
                        { name: 'Img_Dimmed', kind: 'Image (Semi-Transparent)', note: '배경 어둡게' },
                        {
                          name: 'Modal_Window',
                          kind: 'Image (Popup Base)',
                          children: [
                            { name: 'Txt_SelectedSongTitle', kind: 'Text (TMP)' },
                            { name: 'Btn_Easy', kind: 'Button (TMP)', note: '(1) Easy - 색상 원형 그림' },
                            { name: 'Btn_Normal', kind: 'Button (TMP)', note: '(2) Normal - 색상 악보' },
                            { name: 'Btn_Hard', kind: 'Button (TMP)', note: '(3) Hard - 흑백 악보' },
                            { name: 'Btn_Close', kind: 'Button (TMP)' },
                          ],
                        },
                      ],
                    },
                  ],
                },
                {
                  name: 'Cognitive_Panel',
                  kind: 'Empty',
                  note: '2-2. 하단 탭2 (Cognitive) - 인지 게임 화면',
                  children: [
                    {
                      name: 'TopBar',
                      kind: 'Image / Empty',
                      children: [{ name: 'Txt_Title', kind: 'Text (TMP)', note: '"인지 게임"' }],
                    },
                    {
                      name: 'LevelSelect_ScrollView',
                      kind: 'ScrollView',
                      note: '난이도 1~5 선택 영역',
                      children: [
                        {
                          name: 'Viewport',
                          kind: 'Mask',
                          children: [
                            {
                              name: 'Content',
                              kind: 'Empty + GridLayoutGroup',
                              children: [
                                { name: 'Btn_Level1', kind: 'Button (TMP)' },
                                { name: 'Btn_Level2', kind: 'Button (TMP)' },
                                { name: 'Btn_Level3', kind: 'Button (TMP)' },
                                { name: 'Btn_Level4', kind: 'Button (TMP)' },
                                { name: 'Btn_Level5', kind: 'Button (TMP)' },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      name: 'GameDescription_Card',
                      kind: 'Image',
                      note: '난이도별 게임 설명 및 시작',
                      children: [
                        { name: 'Txt_LevelTitle', kind: 'Text (TMP)' },
                        { name: 'Txt_Description', kind: 'Text (TMP)' },
                        { name: 'Btn_StartCognitiveGame', kind: 'Button (TMP)' },
                      ],
                    },
                  ],
                },
                {
                  name: 'Records_Panel',
                  kind: 'Empty',
                  note: '2-3. 하단 탭3 (Records) - 플레이 기록 화면',
                  children: [
                    {
                      name: 'TopBar',
                      kind: 'Image / Empty',
                      children: [{ name: 'Txt_Title', kind: 'Text (TMP)', note: '"플레이 기록"' }],
                    },
                    {
                      name: 'Summary_Card',
                      kind: 'Image',
                      note: '전체 플레이 요약',
                      children: [
                        { name: 'Txt_TotalPlayTime', kind: 'Text (TMP)' },
                        { name: 'Txt_TotalScore', kind: 'Text (TMP)' },
                      ],
                    },
                    {
                      name: 'History_ScrollView',
                      kind: 'ScrollView',
                      note: '상세 플레이 이력',
                      children: [
                        {
                          name: 'Viewport',
                          kind: 'Mask',
                          children: [
                            {
                              name: 'Content',
                              kind: 'Empty + VerticalLayoutGroup',
                              children: [
                                {
                                  name: '[Prefab] RecordItem',
                                  kind: 'Image',
                                  children: [
                                    { name: 'Txt_Date', kind: 'Text (TMP)' },
                                    { name: 'Txt_SongOrGameName', kind: 'Text (TMP)' },
                                    { name: 'Txt_Difficulty', kind: 'Text (TMP)' },
                                    { name: 'Txt_Score', kind: 'Text (TMP)' },
                                  ],
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
                {
                  name: 'MyPage_Panel',
                  kind: 'Empty',
                  note: '3. 마이페이지 화면',
                  children: [
                    {
                      name: 'MyPage_Main_View',
                      kind: 'Empty',
                      note: '마이페이지 메인',
                      children: [
                        {
                          name: 'Profile_Card',
                          kind: 'Image',
                          children: [
                            { name: 'Img_UserAvatar', kind: 'Image' },
                            { name: 'Txt_UserName', kind: 'Text (TMP)' },
                            { name: 'Txt_UserEmail', kind: 'Text (TMP)' },
                          ],
                        },
                        {
                          name: 'MenuList_Group',
                          kind: 'Empty + VerticalLayoutGroup',
                          children: [
                            { name: 'Btn_GoEditProfile', kind: 'Button (TMP)', note: '-> SubView_EditProfile 활성화' },
                            { name: 'Btn_GoNotification', kind: 'Button (TMP)', note: '-> SubView_Notification 활성화' },
                            { name: 'Btn_GoAppSettings', kind: 'Button (TMP)', note: '-> SubView_AppSettings 활성화' },
                            { name: 'Btn_GoNotice', kind: 'Button (TMP)', note: '-> SubView_Notice 활성화' },
                            { name: 'Btn_GoCustomerService', kind: 'Button (TMP)', note: '-> SubView_CustomerService 활성화' },
                          ],
                        },
                      ],
                    },
                    {
                      name: 'SubView_EditProfile',
                      kind: 'Empty',
                      note: '3-1. 프로필 수정',
                      children: [
                        {
                          name: 'SubTopBar',
                          kind: 'Image / Empty',
                          children: [
                            { name: 'Btn_Back', kind: 'Button (TMP)', note: '-> MyPage_Main_View로 돌아가기' },
                            { name: 'Txt_SubTitle', kind: 'Text (TMP)', note: '"프로필 수정"' },
                          ],
                        },
                        { name: 'InputField_Nickname', kind: 'InputField (TMP)' },
                        { name: 'Btn_ChangeAvatar', kind: 'Button (TMP)' },
                        { name: 'Btn_SaveProfile', kind: 'Button (TMP)' },
                      ],
                    },
                    {
                      name: 'SubView_Notification',
                      kind: 'Empty',
                      note: '3-2. 알림 설정',
                      children: [
                        {
                          name: 'SubTopBar',
                          kind: 'Image / Empty',
                          children: [
                            { name: 'Btn_Back', kind: 'Button (TMP)' },
                            { name: 'Txt_SubTitle', kind: 'Text (TMP)', note: '"알림 설정"' },
                          ],
                        },
                        { name: 'Toggle_PushAuth', kind: 'Toggle' },
                        { name: 'Toggle_NightPush', kind: 'Toggle' },
                      ],
                    },
                    {
                      name: 'SubView_AppSettings',
                      kind: 'Empty',
                      note: '3-3. 앱 설정',
                      children: [
                        {
                          name: 'SubTopBar',
                          kind: 'Image / Empty',
                          children: [
                            { name: 'Btn_Back', kind: 'Button (TMP)' },
                            { name: 'Txt_SubTitle', kind: 'Text (TMP)', note: '"앱 설정"' },
                          ],
                        },
                        { name: 'Slider_BGMVolume', kind: 'Slider' },
                        { name: 'Slider_SFXVolume', kind: 'Slider' },
                        { name: 'Txt_AppVersion', kind: 'Text (TMP)' },
                        { name: 'Btn_Logout', kind: 'Button (TMP)' },
                      ],
                    },
                    {
                      name: 'SubView_Notice',
                      kind: 'Empty',
                      note: '3-4. 공지사항',
                      children: [
                        {
                          name: 'SubTopBar',
                          kind: 'Image / Empty',
                          children: [
                            { name: 'Btn_Back', kind: 'Button (TMP)' },
                            { name: 'Txt_SubTitle', kind: 'Text (TMP)', note: '"공지사항"' },
                          ],
                        },
                        {
                          name: 'Notice_ScrollView',
                          kind: 'ScrollView',
                          children: [
                            {
                              name: 'Viewport',
                              kind: 'Mask',
                              children: [
                                {
                                  name: 'Content',
                                  kind: 'Empty + VerticalLayoutGroup',
                                  children: [{ name: '[Prefab] NoticeItem', kind: 'Image' }],
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      name: 'SubView_CustomerService',
                      kind: 'Empty',
                      note: '3-5. 고객센터',
                      children: [
                        {
                          name: 'SubTopBar',
                          kind: 'Image / Empty',
                          children: [
                            { name: 'Btn_Back', kind: 'Button (TMP)' },
                            { name: 'Txt_SubTitle', kind: 'Text (TMP)', note: '"고객센터"' },
                          ],
                        },
                        { name: 'Txt_FaqTitle', kind: 'Text (TMP)' },
                        { name: 'InputField_Inquiry', kind: 'InputField (TMP)' },
                        { name: 'Btn_SubmitInquiry', kind: 'Button (TMP)' },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              name: 'BottomNavigation_Panel',
              kind: 'Image + HorizontalLayoutGroup',
              note: '하단 탭 바 (로그인 후 고정)',
              children: [
                {
                  name: 'Tab_Drum',
                  kind: 'Button',
                  note: '-> 곡 리스트 화면 전환',
                  children: [
                    { name: 'Img_Icon', kind: 'Image' },
                    { name: 'Txt_Label', kind: 'Text (TMP)', note: '"DRUM"' },
                  ],
                },
                {
                  name: 'Tab_Cognitive',
                  kind: 'Button',
                  note: '-> 인지 게임 화면 전환',
                  children: [
                    { name: 'Img_Icon', kind: 'Image' },
                    { name: 'Txt_Label', kind: 'Text (TMP)', note: '"Cognitive"' },
                  ],
                },
                {
                  name: 'Tab_Records',
                  kind: 'Button',
                  note: '-> 기록 화면 전환',
                  children: [
                    { name: 'Img_Icon', kind: 'Image' },
                    { name: 'Txt_Label', kind: 'Text (TMP)', note: '"Records"' },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    {
      name: 'InGameUI_Canvas',
      kind: 'Canvas (Sort Order: 5)',
      note: '인게임 플레이 화면',
      children: [
        {
          name: 'InGame_Drum_Panel',
          kind: 'Empty',
          note: '드럼 게임 플레이',
          children: [
            {
              name: 'Top_Bar',
              kind: 'Empty',
              children: [
                { name: 'Txt_Score', kind: 'Text (TMP)' },
                { name: 'Txt_Combo', kind: 'Text (TMP)' },
                { name: 'Btn_Stop', kind: 'Button (TMP)', note: '일시정지 버튼' },
              ],
            },
            { name: 'Track_Container', kind: 'Empty / RectTransform', note: '노트 떨어지는 영역' },
            { name: 'Easy_Note_Group', kind: 'Empty', note: 'Easy 모드 노트 렌더링' },
            { name: 'Normal_Note_Group', kind: 'Empty', note: 'Normal 모드 색상 악보' },
            { name: 'Hard_Note_Group', kind: 'Empty', note: 'Hard 모드 흑백 악보' },
          ],
        },
        {
          name: 'InGame_Cognitive_Panel',
          kind: 'Empty',
          note: '인지 게임 플레이',
          children: [
            {
              name: 'Top_Bar',
              kind: 'Empty',
              children: [
                { name: 'Txt_Timer', kind: 'Text (TMP)' },
                { name: 'Txt_Level', kind: 'Text (TMP)' },
                { name: 'Btn_Stop', kind: 'Button (TMP)', note: '일시정지 버튼' },
              ],
            },
            { name: 'GamePlay_Area', kind: 'Empty', note: '인지 게임 영역' },
          ],
        },
      ],
    },
    {
      name: 'Popup_Canvas',
      kind: 'Canvas (Sort Order: 10)',
      note: '최상단 팝업 관리',
      children: [
        {
          name: 'Pause_Popup',
          kind: 'Empty',
          note: '게임 중 Stop 클릭 시 팝업',
          children: [
            { name: 'Img_Dimmed', kind: 'Image (Color: Black / Alpha 150)' },
            {
              name: 'Popup_Window',
              kind: 'Image',
              children: [
                { name: 'Txt_Title', kind: 'Text (TMP)', note: '"일시 정지"' },
                { name: 'Btn_ChangeDifficulty', kind: 'Button (TMP)', note: '난이도 변경 선택' },
                { name: 'Btn_Resume', kind: 'Button (TMP)', note: '게임 재개' },
                { name: 'Btn_Exit', kind: 'Button (TMP)', note: '곡 리스트로 나가기' },
              ],
            },
          ],
        },
        {
          name: 'Cognitive_Evaluation_Popup',
          kind: 'Empty',
          note: '인지 게임 종료 후 평가 화면',
          children: [
            { name: 'Img_Dimmed', kind: 'Image' },
            {
              name: 'Popup_Window',
              kind: 'Image',
              children: [
                { name: 'Txt_Title', kind: 'Text (TMP)', note: '"게임 평가 결과"' },
                { name: 'Txt_ReactionTime', kind: 'Text (TMP)', note: '"반응 속도"' },
                { name: 'Txt_Accuracy', kind: 'Text (TMP)', note: '"정확도"' },
                { name: 'Txt_EvaluationGrade', kind: 'Text (TMP)', note: '"평가 등급"' },
                { name: 'Btn_Retry', kind: 'Button (TMP)', note: '"다시하기"' },
                { name: 'Btn_Confirm', kind: 'Button (TMP)', note: '"확인"' },
              ],
            },
          ],
        },
        {
          name: 'System_Alert_Popup',
          kind: 'Empty',
          note: '시스템 알림/에러',
          children: [
            { name: 'Img_Dimmed', kind: 'Image' },
            {
              name: 'Popup_Window',
              kind: 'Image',
              children: [
                { name: 'Txt_Message', kind: 'Text (TMP)' },
                { name: 'Btn_OK', kind: 'Button (TMP)' },
              ],
            },
          ],
        },
      ],
    },
  ],
};

function collectBranchIds(node: TreeNode, id: string, acc: string[]) {
  if (!node.children?.length) return;
  acc.push(id);
  node.children.forEach((child, index) => collectBranchIds(child, `${id}/${index}:${child.name}`, acc));
}

const ROOT_ID = 'root';

function Hierarchy() {
  const allBranchIds = useMemo(() => {
    const acc: string[] = [];
    collectBranchIds(TREE, ROOT_ID, acc);
    return acc;
  }, []);

  const collapsedByDefault = useMemo(
    () => new Set(allBranchIds.filter((id) => id !== ROOT_ID)),
    [allBranchIds],
  );

  const [collapsed, setCollapsed] = useState<Set<string>>(collapsedByDefault);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const copyTimeoutRef = useRef<number | null>(null);

  useEffect(() => () => {
    if (copyTimeoutRef.current) window.clearTimeout(copyTimeoutRef.current);
  }, []);

  const handleCopy = (id: string, name: string) => (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    navigator.clipboard?.writeText(name).catch(() => {});
    setCopiedId(id);
    if (copyTimeoutRef.current) window.clearTimeout(copyTimeoutRef.current);
    copyTimeoutRef.current = window.setTimeout(() => setCopiedId(null), 1200);
  };

  const toggle = (id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleKeyDown = (id: string) => (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggle(id);
    }
  };

  const renderNode = (node: TreeNode, id: string, depth: number) => {
    const hasChildren = !!node.children?.length;
    const isOpen = !collapsed.has(id);

    return (
      <li key={id} className="hierarchy-node">
        <div
          className={`hierarchy-row ${hasChildren ? 'is-branch' : 'is-leaf'}`}
          style={{ paddingLeft: 14 + depth * 24 }}
          onClick={hasChildren ? () => toggle(id) : undefined}
          role={hasChildren ? 'button' : undefined}
          tabIndex={hasChildren ? 0 : undefined}
          aria-expanded={hasChildren ? isOpen : undefined}
          onKeyDown={hasChildren ? handleKeyDown(id) : undefined}
        >
          <span className="hierarchy-toggle">
            {hasChildren && (isOpen ? <CaretDown size={16} weight="bold" /> : <CaretRight size={16} weight="bold" />)}
          </span>
          <span className="hierarchy-icon">
            {hasChildren ? (
              <FolderSimple size={19} weight={isOpen ? 'fill' : 'regular'} />
            ) : (
              <FileText size={17} />
            )}
          </span>
          <span className="hierarchy-name">{node.name}</span>
          <button
            type="button"
            className={`hierarchy-copy${copiedId === id ? ' is-copied' : ''}`}
            onClick={handleCopy(id, node.name)}
            title="이름 복사"
            aria-label={`${node.name} 이름 복사`}
          >
            {copiedId === id ? <Check size={13} weight="bold" /> : <Copy size={13} />}
          </button>
          {node.kind && <span className="hierarchy-kind">{node.kind}</span>}
          {node.note && <span className="hierarchy-note">{node.note}</span>}
        </div>
        {hasChildren && isOpen && (
          <ul className="hierarchy-children">
            {node.children!.map((child, index) => renderNode(child, `${id}/${index}:${child.name}`, depth + 1))}
          </ul>
        )}
      </li>
    );
  };

  return (
    <div className="hierarchy-page">
      <header className="hierarchy-header">
        <div className="hierarchy-heading">
          <h1>Scene Hierarchy</h1>
          <p>MainScene 구조입니다. 항목을 클릭하면 하위 메뉴를 펼치거나 접을 수 있어요. (오브젝트 종류 · 설명 포함)</p>
        </div>
        <div className="hierarchy-actions">
          <button type="button" onClick={() => setCollapsed(new Set())}>
            모두 펼치기
          </button>
          <button type="button" onClick={() => setCollapsed(collapsedByDefault)}>
            모두 접기
          </button>
        </div>
      </header>

      <ul className="hierarchy-tree">{renderNode(TREE, ROOT_ID, 0)}</ul>
    </div>
  );
}

export default Hierarchy;
